// src/App.js
import React, { useEffect, useState, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth as useAuthContext } from "./context/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import ProtectedRoute from "./ProtectedRoute";
import TasksPage from "./pages/TasksPage";
import ActivitySocketListener from "./components/ActivitySocketListener";
import "leaflet/dist/leaflet.css";
import ManagerGroupPage from "./pages/ManagerGroupPage";
import RepGroupPage from "./pages/RepGroupPage";
import UsersPage from "./pages/UsersPage";
import TerritoriesPage from "./pages/TerritoriesPage";
import AddTerritoryPage from "./pages/AddTerritoryPage";
import TerritoryDetailPage from "./pages/TerritoryDetailPage";
import AddTaskPage from "./pages/AddTaskPage";
import FollowUpPage from "./pages/FollowUpPage";
import AddFollowUpPage from "./pages/AddFollowUpPage";
import NotificationsPage from "./pages/NotificationsPage";
import AddNotificationPage from "./pages/AddNotificationPage";
import AddUserPage from "./pages/AddUserPage";
import DashboardV2 from "./pages/DashboardV2";
import AddLeadPage from "./pages/AddLeadPage";
import SavedRoutesPage from "./pages/SavedRoutes";
import ThemeSettingsPage from "./pages/ThemeSettingsPage";
import ProposalDeclinePage from "./pages/ProposalDeclinePage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ReportsPage from "./pages/ReportsPage";
import RepPositionsPage from "./pages/RepPositionsPage";
import ActionLogPage from "./pages/ActionLogPage";  // ✅ Add this line
import LocationUpdater from "./components/LocationUpdater";
import { userIsLegacyDefaultOrganization } from "./utils/legacyDefaultOrganization";
import MessageCenter from "./pages/MessageCenter";
import ConversationsPage from "./pages/ConversationsPage";
import TeamChatPanel from "./components/Layout/TeamChatPanel";
import EditUserPage from "./pages/EditUserPage";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PushNotificationPrompt from "./components/PushNotificationPrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import SearchLeadsPage from "./pages/SearchLeadsPage";
import LeadDetailPage from "./pages/LeadDetailPage";
import SubscriptionSettingsPage from "./pages/SubscriptionSettingsPage";
import CompanyProfilePage from "./pages/CompanyProfilePage";
import PayscalesPage from "./pages/PayscalesPage";
import RepPayScalesPage from "./pages/RepPayScalesPage";
import SalesTeamsPage from "./pages/SalesTeamsPage";
import ProductsServicesPage from "./pages/ProductsServicesPage";
import CompanySignupPage from "./pages/CompanySignupPage";
import FollowUpIntelligencePage from "./pages/FollowUpIntelligencePage";
import FollowUpSettingsPage from "./pages/FollowUpSettingsPage";
import ChangelogPage from "./pages/ChangelogPage";
import SalesPipelinePage from "./pages/SalesPipelinePage";
import RolesReadOnlyPage from "./pages/RolesReadOnlyPage";
import useAuth from "./hooks/useAuth";


import { TourProvider } from "./tour/TourProvider";
import TourOverlay from "./tour/TourOverlay";
import ProposalThankYouPage from "./pages/ProposalThankYouPage";
import ProposalUnavailablePage from "./pages/ProposalUnavailablePage";
import ProposalCustomerPage from "./pages/ProposalCustomerPage";

import ErrorBoundary from "./components/ErrorBoundary";

// Layout
import CustomThemeProvider from "./components/Layout/ThemeProvider";
import DashboardLayout from "./components/Layout/DashboardLayout";
import { ToastProvider } from "./context/GlobalToastContext";
import { GlobalCountProvider } from "./context/GlobalCountContext";
import ResponsiveSidebarWrapper from "./components/Layout/ResponsiveSidebarWrapper";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useMobileAddressBar from "./hooks/useMobileAddressBar";



function AppShell() {
  const navigate = useNavigate();
  const { bootChecked, isAuthenticated } = useAuthContext();
  const { user: authUser, role: authRole } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
const params = new URLSearchParams(window.location.search);
    const proposalId = params.get("acceptProposal");
const hasAcceptProposal = Boolean(proposalId);


  useEffect(() => {
  if (!bootChecked) return;

  if (proposalId) {
    navigate(`/thank-you/proposal`, { replace: true });
  }
}, [bootChecked, proposalId, navigate]);


  useMobileAddressBar(); // Mobile-only: encourage address bar collapse

  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentUser(null);
      return;
    }

    const storedUser = localStorage.getItem("user");
    if (!storedUser || storedUser === "undefined" || storedUser === "null") {
      setCurrentUser(null);
      return;
    }

    try {
      setCurrentUser(JSON.parse(storedUser));
    } catch (err) {
      console.error("Failed to parse stored user:", err);
      localStorage.removeItem("user");
      setCurrentUser(null);
    }
  }, [isAuthenticated]);


  const token = localStorage.getItem("access_token");

  const isMainOrgAdmin =
    authRole === "Admin" &&
    userIsLegacyDefaultOrganization(authUser);
  const canAccessRoleSettings =
    authUser?.is_superuser === true || isMainOrgAdmin;

  if (!bootChecked) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f4f6f8",
          color: "#1e293b",
          fontWeight: 600,
        }}
      >
        Loading…
      </div>
    );
  }

  return (

 <TourProvider>
    <TourOverlay />
    <GlobalCountProvider>
      {isAuthenticated && currentUser?.id && (
        <ActivitySocketListener token={token} userId={currentUser.id} />
      )}

      {/* Add LocationUpdater for authenticated users */}
      {isAuthenticated && token && (
        <LocationUpdater token={token} intervalMinutes={5} />
      )}

      {/* PWA Components */}
      <PWAInstallPrompt />
      {isAuthenticated && token && <PushNotificationPrompt />}
      <OfflineIndicator />

     

      <ResponsiveSidebarWrapper>
        <ErrorBoundary>
          <Routes>
            {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
		<Route path="/thank-you/proposal" element={<ProposalThankYouPage />} />
		<Route path="/thank-you/proposal-unavailable" element={<ProposalUnavailablePage />} />
		<Route path="/proposal/decline/:leadId" element={<ProposalDeclinePage />} />
		<Route path="/proposal/:proposalId" element={<ProposalCustomerPage />} />
	  <Route path="/register" element={<Navigate to="/signup/company" replace />} />
	  <Route path="/signup/company" element={<CompanySignupPage />} />


          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard token={token} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/sales-pipeline"
            element={
              <ProtectedRoute>
                <SalesPipelinePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Sales Director"]}>
                <UsersPage />
              </ProtectedRoute>
            }
          />

<Route
  path="/users/:id/edit"
  element={
    <ProtectedRoute>
      <EditUserPage />
    </ProtectedRoute>
  }
/>

          {/* Deprecated: Tasks is an earlier CRM iteration. Keep the route until nothing else opens it. */}
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/territories"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Sales Director", "Manager"]}>
                <TerritoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/territory/add"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Sales Director", "Manager"]}>
                <AddTerritoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/territories/territorydetail"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Sales Director", "Manager"]}>
                <TerritoryDetailPage />
              </ProtectedRoute>
            }
          />


          <Route
            path="/follow-up-intelligence"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Sales Director", "Manager"]}>
                <FollowUpIntelligencePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/follow-up-intelligence/settings"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Sales Director"]}>
                <FollowUpSettingsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/followups"
            element={
              <ProtectedRoute>
                <FollowUpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/followups/add"
            element={
              <ProtectedRoute>
                <AddFollowUpPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications/add"
            element={
              <ProtectedRoute>
                <AddNotificationPage />
              </ProtectedRoute>
            }
          />

{/* Deprecated: removed from the sidebar. Keep the route until SMS and other links no longer open it. */}
<Route
  path="/message-center"
  element={
    <ProtectedRoute>
      <MessageCenter />
    </ProtectedRoute>
  }
/>

<Route
  path="/conversations"
  element={
    <ProtectedRoute>
      <ConversationsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/team-chat"
  element={
    <ProtectedRoute>
      <TeamChatPanel />
    </ProtectedRoute>
  }
/>

   

<Route
  path="/settings/roles"
  element={
    canAccessRoleSettings ? (
      <ProtectedRoute>
        <RolesReadOnlyPage />
      </ProtectedRoute>
    ) : (
      <Navigate to="/dashboard" replace />
    )
  }
/>


{/* Add this route after /settings/theme */}
<Route
  path="/settings/reports"
  element={
    <ProtectedRoute allowedRoles={["Admin", "Sales Director", "Manager"]}>
      <ReportsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/integrations"
  element={
    <ProtectedRoute allowedRoles={["Admin"]}>
      <EmailSettingsPage />
    </ProtectedRoute>
  }
/>
<Route
  path="/settings/email"
  element={
    <ProtectedRoute allowedRoles={["Admin"]}>
      <Navigate to="/settings/integrations" replace />
    </ProtectedRoute>
  }
/>


          <Route
            path="/users/new"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Sales Director"]}>
                <AddUserPage />
              </ProtectedRoute>
            }
          />
          {/* Rep-Positions */}
          <Route
            path="/rep-positions"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Sales Director", "Manager"]}>
                <RepPositionsPage />
              </ProtectedRoute>
            }
          />

<Route
  path="/settings/company-profile"
  element={
    <ProtectedRoute allowedRoles={["Admin", "Sales Director"]}>
      <CompanyProfilePage />
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/payscales"
  element={
    <ProtectedRoute allowedRoles={["Admin"]}>
      <PayscalesPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/rep-pay-scales"
  element={
    <ProtectedRoute allowedRoles={["Admin"]}>
      <RepPayScalesPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/sales-teams"
  element={
    <ProtectedRoute allowedRoles={["Admin"]}>
      <SalesTeamsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/products-services"
  element={
    <ProtectedRoute allowedRoles={["Admin", "Sales Director"]}>
      <ProductsServicesPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/settings/subscription"
  element={
    <ProtectedRoute allowedRoles={["Admin"]}>
      <SubscriptionSettingsPage />
    </ProtectedRoute>
  }
/>



<Route
  path="/settings/action-log"
  element={
    <ProtectedRoute allowedRoles={["Admin", "Sales Director"]}>
      <ActionLogPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/changelog"
  element={
    <ProtectedRoute>
      <ChangelogPage />
    </ProtectedRoute>
  }
/>
          <Route
            path="/add-followup"
            element={
              <ProtectedRoute>
                <AddFollowUpPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/leadmap/savedroutes"
            element={
              <ProtectedRoute>
                <SavedRoutesPage token={token} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/territory/add/lead"
            element={
              <ProtectedRoute>
                <AddLeadPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard-v2"
            element={
              <ProtectedRoute>
                <DashboardV2 />
              </ProtectedRoute>
            }
          />

          {/* Deprecated with /tasks. */}
          <Route
            path="/tasks/add"
            element={
              <ProtectedRoute>
                <AddTaskPage />
              </ProtectedRoute>
            }
          />

<Route
  path="/leads/search"
  element={
    <ProtectedRoute>
      <SearchLeadsPage />
    </ProtectedRoute>
  }
/>

          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <Leads token={token} currentUser={currentUser} />
              </ProtectedRoute>
            }
          />


<Route
  path="/leads/:id"
  element={
    <ProtectedRoute>
      <LeadDetailPage />
    </ProtectedRoute>
  }
/>

          {/* Manager Group & Rep Group */}
          <Route
            path="/groups/manager"
            element={
              <ProtectedRoute>
                <ManagerGroupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/rep"
            element={
              <ProtectedRoute>
                <RepGroupPage />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />


          </Routes>
        </ErrorBoundary>
      </ResponsiveSidebarWrapper>
    </GlobalCountProvider>
 </TourProvider>
  );
}

function App() {
  return (
    <CustomThemeProvider>
      <ToastProvider>
        <Router>
          <AuthProvider>
            <AppShell />
          </AuthProvider>
        </Router>
      </ToastProvider>
    </CustomThemeProvider>
  );
}

export default App;
